import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useMsal } from '@azure/msal-react'
import { deriveFirstName, loadUserProfile } from '@/services/graph/graphService'

export interface UserProfileValue {
  firstName: string
  displayName: string
  /** Object URL for the Graph profile photo, or null when unavailable. */
  photoUrl: string | null
  loading: boolean
}

const emptyProfile: UserProfileValue = {
  firstName: '',
  displayName: '',
  photoUrl: null,
  loading: true,
}

const UserProfileContext = createContext<UserProfileValue>(emptyProfile)

/**
 * Fetches the signed-in user's Graph profile + photo once and shares it with
 * the whole authenticated app (top-right menu, chat avatars) so the photo is
 * only downloaded a single time.
 */
export function UserProfileProvider({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal()
  const account = accounts[0]

  const [value, setValue] = useState<UserProfileValue>(() => ({
    firstName: deriveFirstName(account?.name, account?.username),
    displayName: account?.name || account?.username || '',
    photoUrl: null,
    loading: true,
  }))

  useEffect(() => {
    if (!account) return
    let cancelled = false
    let createdUrl: string | null = null

    void loadUserProfile(instance, account)
      .then((profile) => {
        if (cancelled) return
        createdUrl = profile.photoUrl
        setValue({
          firstName: profile.firstName,
          displayName: profile.displayName,
          photoUrl: profile.photoUrl,
          loading: false,
        })
      })
      .catch(() => {
        if (!cancelled) setValue((prev) => ({ ...prev, loading: false }))
      })

    return () => {
      cancelled = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [instance, account])

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>
}

export function useUserProfile(): UserProfileValue {
  return useContext(UserProfileContext)
}
